import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { toast } from 'sonner'

import { AppRoutes } from '@/constants/appRoutes'
import { catchWithSentry } from '@/hooks/useCatchWithSentry'
import { usePdfDownload } from '@/hooks/usePdfDownload'
import {
  BurgerMenu,
  type BurgerMenuItem,
} from '@/pages/Reports/components/BurgerMenu'
import { ConsentFilesSheet } from '@/pages/Reports/components/ConsentFilesSheet'
import {
  useGenerateAnonymousPdfMutation,
  useGenerateExpandedPdfMutation,
  useMakeBackgroundCheckOpenedMutation,
} from '@/store/api/Person/Person.query'
import { EReportType } from '@/store/api/Person/Person.types'
import { selectWorkspaceCreditsFromStore } from '@/store/api/Users/Users.selector'
import { createSafeWindowOpener } from '@/utils/openUrlSafely'

interface ActionsTypes {
  id: string
  subjectId: string
  reportType?: EReportType
  pdf?: string
  expandedPdf?: string
  anonymousPdf?: string
  complianceSigned?: string
  summarySigned?: string
  disclosureSigned?: string
  dynamicSigned?: string
  otherAuthorizationSigned?: string
}

export const Actions = ({
  id,
  subjectId,
  reportType,
  pdf,
  expandedPdf,
  anonymousPdf,
  complianceSigned,
  summarySigned,
  disclosureSigned,
  dynamicSigned,
  otherAuthorizationSigned,
}: ActionsTypes) => {
  const { t } = useTranslation()
  const navigate = useNavigate()

  const [makeBackgroundCheckOpened] = useMakeBackgroundCheckOpenedMutation()
  const [generateAnonymousPdf, { isLoading: isLoadingAnonymousPdf }] =
    useGenerateAnonymousPdfMutation()
  const [generateExpandedPdf, { isLoading: isLoadingExpandedPdf }] =
    useGenerateExpandedPdfMutation()
  const { downloadPdf, isDownloading } = usePdfDownload({ socialCheckId: id })

  const workspaceCredits = useSelector(selectWorkspaceCreditsFromStore)
  const aiAnalystReportCredits = workspaceCredits?.aiAnalystReportCredits ?? 0
  const adverseMediaReportCredits =
    workspaceCredits?.adverseMediaReportCredits ?? 0
  const assistedReportCredits = workspaceCredits?.assistedReportCredits ?? 0
  const aiReportCredits = workspaceCredits?.aiReportCredits ?? 0

  const hasSignedConsents =
    !!complianceSigned ||
    !!summarySigned ||
    !!disclosureSigned ||
    !!dynamicSigned ||
    !!otherAuthorizationSigned

  const canFollowUp =
    reportType === EReportType.AiAnalyst ||
    reportType === EReportType.AdverseMedia ||
    reportType === EReportType.Assisted ||
    reportType === EReportType.Ai

  const handleDownload = async () => {
    if (pdf) await downloadPdf()
    try {
      await makeBackgroundCheckOpened({ id }).unwrap()
    } catch (e) {
      catchWithSentry(e)
    }
  }

  // Generation can outlive the request: the backend keeps working and stores the
  // PDF, so a timeout is worth telling apart from an outright failure.
  const notifyPdfGenerationFailed = (e?: any) => {
    toast.error(
      e?.status === 408
        ? t('feedback.toast.pdfGenerationStillRunning')
        : t('feedback.toast.pdfGenerationFailed'),
    )
  }

  const handleDownloadPrivatePdf = async () => {
    if (anonymousPdf) {
      await downloadPdf('anonymousPdf')
    } else {
      const opener = createSafeWindowOpener()
      try {
        const res = await generateAnonymousPdf({ id: subjectId }).unwrap()
        if (res.info.pdf) {
          opener.open(res.info.pdf)
        } else {
          opener.close()
          notifyPdfGenerationFailed()
        }
      } catch (e) {
        opener.close()
        notifyPdfGenerationFailed(e)
        catchWithSentry(e)
      }
    }
    try {
      await makeBackgroundCheckOpened({ id }).unwrap()
    } catch (e) {
      catchWithSentry(e)
    }
  }

  const handleDownloadExpandedPdf = async () => {
    if (expandedPdf) {
      await downloadPdf('expandedPdf')
    } else {
      const opener = createSafeWindowOpener()
      try {
        const res = await generateExpandedPdf({ id: subjectId }).unwrap()
        if (res.info.pdf) {
          opener.open(res.info.pdf)
        } else {
          opener.close()
          notifyPdfGenerationFailed()
        }
      } catch (e) {
        opener.close()
        notifyPdfGenerationFailed(e)
        catchWithSentry(e)
      }
    }
    try {
      await makeBackgroundCheckOpened({ id }).unwrap()
    } catch (e) {
      catchWithSentry(e)
    }
  }

  const handleFollowUp = async () => {
    if (reportType === EReportType.FacialMatching) {
      toast.error(t('dashboard.reportsScreen.followUpReportNotAvailable'))
      return
    }
    const noCreditsForType =
      (reportType === EReportType.AiAnalyst && aiAnalystReportCredits <= 0) ||
      (reportType === EReportType.AdverseMedia &&
        adverseMediaReportCredits <= 0) ||
      (reportType === EReportType.Assisted && assistedReportCredits <= 0) ||
      (reportType === EReportType.Ai && aiReportCredits <= 0)
    if (noCreditsForType) {
      toast.error(t('feedback.toast.noCredits'))
      return
    }
    try {
      await makeBackgroundCheckOpened({ id }).unwrap()
    } catch (e) {
      catchWithSentry(e)
    }
    navigate(`${AppRoutes.newFollowUpReport}?reportId=${id}`)
  }

  const items: BurgerMenuItem[] = []

  if (pdf) {
    items.push({
      kind: 'action',
      label: t('dashboard.reportsScreen.download'),
      onClick: handleDownload,
      isLoading: isDownloading,
    })
    items.push({
      kind: 'action',
      label: t('dashboard.reportsScreen.generatePrivatePDdf'),
      onClick: handleDownloadPrivatePdf,
    })
    items.push({
      kind: 'action',
      label: t('dashboard.reportsScreen.generateExpandedPDdf'),
      onClick: handleDownloadExpandedPdf,
    })
  }

  if (hasSignedConsents) {
    items.push({
      kind: 'custom',
      render: () => (
        <ConsentFilesSheet
          isLoading={isDownloading}
          variant="ghost"
          paddingLeft={8}
          customWidth="100%"
          customJustifyContent="flex-start"
          complianceSigned={complianceSigned}
          summarySigned={summarySigned}
          disclosureSigned={disclosureSigned}
          dynamicSigned={dynamicSigned}
          otherAuthorizationSigned={otherAuthorizationSigned}
        />
      ),
    })
  }

  if (canFollowUp) {
    items.push({
      kind: 'action',
      label: t('dashboard.reportsScreen.followUpReport'),
      onClick: handleFollowUp,
    })
  }

  if (items.length === 0) return null

  return (
    <BurgerMenu
      items={items}
      showLoadingOverlay={isLoadingAnonymousPdf || isLoadingExpandedPdf}
      loadingOverlayText={t('dashboard.reportsScreen.loadingOverlay')}
    />
  )
}
